<script>
  import { getContext } from "svelte";
  import { navigate } from "svelte-routing";
  import { copyToClipboard } from "overline";
  import get from "lodash/get";
  import set from "lodash/set";
  import unset from "lodash/unset";
  import cloneDeep from "lodash/cloneDeep";

  import LinkIcon from "@local/assets/icons/link.svg";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import IntegerInput from "#lib/sidebar/IntegerInput.svelte";
  import MapboxInput from "#lib/sidebar/MapboxInput.svelte";
  import GuardedTextInput from "#lib/sidebar/GuardedTextInput.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import BooleanInput from "#lib/sidebar/BooleanInput.svelte";
  import ColorInput from "#lib/sidebar/ColorInput.svelte";
  import TextareaInput from "#lib/sidebar/TextareaInput.svelte";
  import ImageUploader from "#lib/ImageUploader.svelte";
  import JSONInput from "#lib/sidebar/JSONInput.svelte";

  import { profile } from "#src/stores/auth.js";
  import { SUPABASE_URL as supabaseUrl } from "#src/env";
  import { api, createNotionCustomer } from "#src/api";
  import { alerts } from "#src/stores/alerts.js";
  import { getCustomerId } from "#src/api/stripe.js";

  export let org;
  export let manageable = false;

  const productLists = getContext("productLists");

  let customerId;
  let notionOrgNumber;
  let notionId;
  let creatingCustomer = false;

  $: capabilities = $profile?.profile_capabilities?.capabilities || {};
  $: canEditAdvancedSettings = capabilities.edit_advanced_org_settings;
  $: canEditBasicSettings = capabilities.edit_basic_org_settings;

  function uploadLogo(e) {
    if (e.detail.type && e.detail.base64) {
      const data = cloneDeep($org.data);
      set(data, "logo", e.detail);
      org.updateProp("data", data);
    }
  }

  function updateDevOrg() {
    if (!$org) return;
    $profile.organization_id = $org.id;
    $profile.organization = $org;
    localStorage.setItem("currentOrg", $org.id);
  }

  function deleteLogo() {
    const data = cloneDeep($org.data);
    unset(data, "logo");
    org.updateProp("data", data);
  }

  function bannerSrc(banner) {
    if (!banner) return null;
    if (banner.base64) return banner.base64;
    if (banner.object_id) {
      return `${supabaseUrl}/storage/v1/render/image/public/images/${banner.object_id}?width=200&height=200&resize=contain`;
    }
  }

  async function uploadBanner(e) {
    if (e.detail.type && e.detail.data) {
      const uuid = crypto.randomUUID();
      const filename = `${uuid}/${e.detail.filename}`;
      const { data: result, error } = await api.storage.from("images").upload(filename, e.detail.data, {
        contentType: e.detail.contentType,
      });

      if (!error) {
        const data = cloneDeep($org.data);
        set(data, "banner", {
          id: uuid,
          object_id: result.path,
          content_type: e.detail.contentType,
          filename: e.detail.filename,
        });
        org.updateProp("data", data);
      }
    }
  }
  function updateOrgData(prop, value) {
    const data = cloneDeep($org.data);
    set(data, prop, value);
    org.updateProp("data", data);
  }

  function deleteBanner() {
    const data = cloneDeep($org.data);
    unset(data, "banner");
    org.updateProp("data", data);
  }

  async function createCustomer() {
    creatingCustomer = true;
    const res = await createNotionCustomer($org.id);
    if (!res.error) {
      const { notion_org_id, notion_org_number } = res.data;
      notionId = notion_org_id;
      notionOrgNumber = notion_org_number;
      alerts.add({ type: "ok", message: "Notion/Negotium org created successfully." });
    } else {
      alerts.add({ type: "error", message: "Failed to create Notion/Negotium org." });
    }
    creatingCustomer = false;
  }

  async function fetchCustomerInfo() {
    const res = await getCustomerId($org.id);
    customerId = res.customer_id;
    notionId = res.notion_id;
    notionOrgNumber = res.number;
  }
</script>

<div class="px-4 pb-4 border-b border-gray-300">
  <h2>{$org.name}</h2>
</div>

<div class="py-4 sm:px-4 space-y-8 text-sm">
  <div class="w-full sm:w-96 space-y-4">
    {#if capabilities.switch_organization && $profile.organization_id !== $org.id}
      <button class="btn btn-danger w-full" on:click={updateDevOrg}
        >Act as a member of this Organization</button>
    {/if}
    <div class="space-y-1">
      <h3 class="p-2">Basic Information</h3>
      <TextInput
        labelWidth="10rem"
        border
        label="Name"
        value={$org.name}
        disabled={!canEditBasicSettings}
        on:input={(e) => org.updateProp("name", e.detail.value)} />
      <TextInput
        labelWidth="10rem"
        border
        label="Primary Email"
        value={$org.primary_contact_email}
        disabled={!canEditBasicSettings}
        on:input={(e) => org.updateProp("primary_contact_email", e.detail.value.trim())} />
      <MapboxInput
        labelWidth="10rem"
        border
        label="Address"
        value={$org.address}
        coordinates={$org.address_coordinates}
        disabled={!canEditBasicSettings}
        on:input={(e) =>
          org.updateProp({
            address: e.detail.value,
            address_coordinates: e.detail.coordinates,
            address_postal_code: e.detail.postal_code,
            address_country_code: e.detail.country_code,
          })} />
      <ColorInput
        border
        label="Accent Color"
        labelWidth="10rem"
        value={$org.accent_color}
        disabled={!canEditBasicSettings}
        on:input={(e) => org.updateProp("accent_color", e.detail.value)} />
      <ImageUploader
        label="Company Logo"
        value={get($org.data, "logo.base64")}
        accept="image/png, image/jpeg"
        disabled={!canEditBasicSettings}
        on:upload={uploadLogo}
        on:delete={deleteLogo} />
      {#if $org.is_public && $org.org_type === "supplier"}
        <ImageUploader
          label="Banner Image"
          value={bannerSrc($org.data?.banner)}
          accept="image/png, image/jpeg"
          uploadType="arraybuffer"
          disabled={!canEditBasicSettings}
          on:upload={uploadBanner}
          on:delete={deleteBanner} />
      {/if}
    </div>
    {#if canEditAdvancedSettings}
      {#await fetchCustomerInfo() then _}
        <div class="space-y-1">
          <h3 class="p-2">Advanced Settings</h3>
          <SelectInput
            labelWidth="10rem"
            border
            label="Org Type"
            value={$org.org_type}
            disabled={!canEditAdvancedSettings}
            options={[
              { label: "Anonymous", value: "anonymous" },
              { label: "Guest", value: "guest" },
              { label: "Recipient", value: "recipient" },
              { label: "Supplier", value: "supplier" },
              { label: "Product Rep", value: "product_rep" },
            ]}
            on:input={(e) => org.updateProp("org_type", e.detail.value)} />
          <SelectInput
            labelWidth="10rem"
            border
            label="SHPX Subscription Type"
            value={$org.shpx_subscription_type}
            disabled={!canEditAdvancedSettings}
            options={[
              { label: "None", value: null },
              { label: "Pro", value: "pro" },
              { label: "Team Starter", value: "team_starter" },
              { label: "Team Pro", value: "team_pro" },
            ]}
            on:input={(e) => org.updateProp("shpx_subscription_type", e.detail.value)} />
          <SelectInput
            labelWidth="10rem"
            border
            label="Default UX Mode"
            value={$org.default_ux_mode}
            disabled={!canEditAdvancedSettings}
            options={[
              { label: "Quoter", value: "quoter" },
              { label: "Reviewer", value: "reviewer" },
              { label: "PDF Exchange", value: "pdf_exchange" },
            ]}
            on:input={(e) => org.updateProp("default_ux_mode", e.detail.value)} />
          <BooleanInput
            labelWidth="10rem"
            border
            label="Allow Org Switching"
            value={$org.allow_org_switching}
            disabled={!canEditAdvancedSettings}
            on:input={(e) => org.updateProp("allow_org_switching", e.detail.value)} />
          {#if $org.org_type === "supplier" || $org.org_type === "product_rep"}
            <BooleanInput
              labelWidth="10rem"
              border
              label="Show Price Calculator"
              value={$org.show_price_calculator}
              disabled={!canEditAdvancedSettings}
              on:input={(e) => org.updateProp("show_price_calculator", e.detail.value)} />
          {/if}
          <JSONInput
            labelWidth="10rem"
            border
            label="Item Templates"
            value={$org.data.templates}
            disabled={!canEditAdvancedSettings}
            on:input={(e) => updateOrgData("templates", e.detail.value)} />
        </div>

        <div class="space-y-1">
          <h3 class="p-2">Configuration</h3>
          {#if $org?.shpx_subscription_type}
            <BooleanInput
              labelWidth="10rem"
              border
              label="Enable SHPX Assist"
              value={$org.shpx_assist_enabled}
              disabled={!canEditAdvancedSettings}
              on:input={(e) => org.updateProp("shpx_assist_enabled", e.detail.value)} />
          {/if}
          {#if $org.default_ux_mode === "reviewer"}
            <BooleanInput
              labelWidth="10rem"
              border
              label="Enable Tally Requests"
              value={$org.tally_request_enabled}
              disabled={!canEditAdvancedSettings}
              on:input={(e) => org.updateProp("tally_request_enabled", e.detail.value)} />
            {#if $org.tally_request_enabled}
              <div class="relative">
                <BooleanInput
                  labelWidth="10rem"
                  border
                  label="Public Tally Request"
                  value={$org.allow_public_tally_request}
                  disabled={!canEditAdvancedSettings}
                  on:input={(e) => org.updateProp("allow_public_tally_request", e.detail.value)} />
                <button
                  class="absolute right-0 top-0 mt-1.5 mr-2 p-1 rounded hover:bg-gray-200"
                  on:click={() => {
                    copyToClipboard(`${window.location.origin}/tally-request?org_id=${$org.id}`);
                  }}>
                  <LinkIcon />
                </button>
              </div>
            {/if}
          {/if}
          {#if !notionId}
            <button class="btn btn-primary w-full" on:click={createCustomer} disabled={creatingCustomer}
              >Create Notion/Negotium Org</button>
          {/if}

          {#if $org.default_ux_mode === "reviewer"}
            {#if $org.tally_request_enabled}
              <IntegerInput
                labelWidth="10rem"
                border
                label="Notion Org ID"
                min={1}
                incrementable={false}
                value={notionOrgNumber}
                disabled />
              <!-- The org's folder in the Tally shared drive, by Drive id (not name). Filled in on the first request. -->
              <TextInput
                labelWidth="10rem"
                border
                label="Tally Drive Folder ID"
                placeholder="Set on first request"
                value={$org.tally_upload_folder}
                disabled={!canEditAdvancedSettings}
                on:input={(e) => org.updateProp("tally_upload_folder", e.detail.value.trim())} />
            {/if}
          {/if}

          {#if notionId}
            <TextInput labelWidth="10rem" border disabled label="Stripe Customer ID" value={customerId} />
          {/if}
          {#if $org.is_public && $org.org_type === "supplier"}
            <GuardedTextInput
              labelWidth="10rem"
              border
              label="Portal Slug"
              value={$org.slug}
              disabled={$profile?.user_role !== "developer"}
              on:input={(e) => org.updateProp("slug", (e.detail.value || "").toLowerCase())} />
            <BooleanInput
              labelWidth="10rem"
              border
              label="Accepts Customers"
              value={$org.accepts_public_membership_requests}
              disabled={!canEditAdvancedSettings}
              on:input={(e) => org.updateProp("accepts_public_membership_requests", e.detail.value)} />
            <TextInput
              labelWidth="10rem"
              border
              label="Default Item Mark"
              value={$org.default_item_mark}
              disabled={!canEditAdvancedSettings}
              on:input={(e) => org.updateProp("default_item_mark", e.detail.value)} />
            <SelectInput
              labelWidth="10rem"
              border
              label="Default Products"
              value={$org.default_products}
              disabled={!canEditAdvancedSettings}
              options={[
                { label: "All Published Products", value: "all" },
                { label: "Generic Products Only", value: "none" },
                { label: "Named Product List", value: "product_list" },
              ]}
              on:input={(e) => org.updateProp("default_products", e.detail.value)} />
            {#if $org.default_products === "product_list"}
              <SelectInput
                labelWidth="10rem"
                border
                label="Product List"
                value={$org.default_product_list_id}
                disabled={!canEditAdvancedSettings}
                options={$productLists?.map((l) => ({ label: l.name, value: l.id }))}
                on:input={(e) => org.updateProp("default_product_list_id", e.detail.value)} />
            {/if}
          {/if}
        </div>
      {/await}
    {/if}
    {#if $org.is_public && $org.org_type === "supplier"}
      <TextareaInput
        fullwidth
        placeholder="Portal Description"
        value={$org.description}
        disabled={!canEditBasicSettings}
        on:input={(e) => org.updateProp("description", e.detail.value)}
        rows={4} />
    {/if}

    {#if manageable && $org.org_type === "supplier"}
      <div class="py-2">
        <button class="btn btn-primary w-full" on:click={() => navigate("/products")}>Manage Products</button>
      </div>
    {/if}
  </div>
</div>
